import React, { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useParticipantOrderDetail } from '@/modules/ParticipantPortal/hooks/useParticipantOrderDetail';
import { useGetOrderByIdQuery } from '@/api/OrderInvoiceQuery';
import type { MostRecentModel } from '@/common/interfaces/mostRecent.interface';
import {
  TRACK_ORDER_PARTICIPANT_QUERY_PARAM,
  getOrderDetailPath,
  getParticipantOrderDetailPath,
  getTrackOrderIdFromPath,
} from '@/constants/Order';
import TrackOrders from '@/modules/Orders/TrackOrders/TrackOrders';

interface ITrackOrderFromUrlProps {
  /**
   * Client HQ loads `api/cp/order/:id`, or the participant order when the url carries
   * `?participantId=`. The participant portal always loads the participant order.
   */
  mode: 'client' | 'participant';
}

/**
 * Opens the track order overlay whenever the url is `/track/:orderId`,
 * for the order id in that path.
 */
const TrackOrderFromUrl: React.FC<ITrackOrderFromUrlProps> = ({ mode }) => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const trackOrderId = getTrackOrderIdFromPath(pathname);
  const hqParticipantId =
    mode === 'client' ? searchParams.get(TRACK_ORDER_PARTICIPANT_QUERY_PARAM) : null;

  // Participant portal paths are keyed by participant id; HQ participant purchases carry it in the query.
  const participantId = mode === 'participant' ? trackOrderId : hqParticipantId;
  const isClientOrder = mode === 'client' && !hqParticipantId;

  const clientOrder = useGetOrderByIdQuery(trackOrderId ?? '', {
    skip: !isClientOrder || !trackOrderId,
  });
  const participantOrder = useParticipantOrderDetail(
    trackOrderId && participantId ? participantId : undefined
  );

  const requestFailed = isClientOrder ? clientOrder.isError : participantOrder.isError;
  const hasFailed = Boolean(trackOrderId) && requestFailed;

  /** A failed track request opens the order page for the id in the url. */
  useEffect(() => {
    if (!trackOrderId || !hasFailed) {
      return;
    }

    navigate(getOrderDetailPath(trackOrderId), { replace: true });
  }, [hasFailed, navigate, trackOrderId]);

  if (!trackOrderId) {
    return null;
  }

  if (participantId) {
    if (!participantOrder.view) {
      return null;
    }

    const closeRedirectPath = hqParticipantId
      ? getParticipantOrderDetailPath(hqParticipantId)
      : getOrderDetailPath(participantId);

    return (
      <TrackOrders
        mostRecentData={participantOrder.view.headerData}
        id={participantOrder.view.displayId}
        trackRouteId={trackOrderId}
        closeRedirectPath={closeRedirectPath}
        showParticipantsOverlay={false}
      />
    );
  }

  if (!clientOrder.data) {
    return null;
  }

  return (
    <TrackOrders
      mostRecentData={clientOrder.data as MostRecentModel}
      individualShipping={clientOrder.data.individualShipping}
      groupOrderDate={clientOrder.data.groupOrderDate}
      isNewGroupOrder={clientOrder.data.newGroupOrder}
      id={clientOrder.data.orderId}
      trackRouteId={trackOrderId}
      closeRedirectPath={getOrderDetailPath(trackOrderId)}
      showParticipantsOverlay={false}
    />
  );
};

TrackOrderFromUrl.displayName = 'TrackOrderFromUrl';

export default TrackOrderFromUrl;
